import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, EmptyState, Field, Input, LoadingBlock, Skeleton, Switch } from "./ui";
import { ApiError } from "../lib/api";
import { absoluteTime, formatBytes, relativeTime } from "../lib/format";
import {
  useBackupDownload,
  useBackupFiles,
  useBackupSchedule,
  useRestores,
  useRunBackup,
  useSetBackupEnabled,
} from "../lib/queries";
import type { BackupFrequency, Database } from "../lib/types";
import { RestoreActivity, RestoreModal, type RestoreChoice } from "./RestoreBackup";
import { RestorePoints } from "./RestorePoints";

/**
 * Backups for one database.
 *
 * Storage, frequency and retention are the platform's — chosen per server by
 * Maxlayer, inherited by every database on it. What a customer decides is on or
 * off, and on MongoDB which database to dump; it costs 30% of the database's
 * plan per hour while it is on.
 *
 * Three tiers, in the order somebody reads them: the switch and what it costs,
 * then four numbers that answer "is this working" without scrolling, then the
 * dumps themselves. The dumps are the only proof any of it works, so each one
 * shows its size and age rather than only a filename — a 20-byte dump and a
 * 2 GB one look identical without the size, which is exactly how MongoDB
 * backups went unnoticed while dumping nothing.
 *
 * Redis is stated here rather than only in documentation, because this is where
 * someone decides whether their data is safe and Dokploy cannot back it up at
 * all.
 *
 * **Every dump restores in one click** — into this database or another of the
 * same engine — for `admin` and above, as deleting a database is. The target's
 * current data is backed up first, so the restore card always offers an undo.
 * The restore points (`RestorePoints`, a timeline by day) therefore show
 * whenever there are dumps, not only while backups are on: a restore's safety
 * copy lands here even for a database whose schedule is off, and it is the undo.
 */

/** The API's frequency values, mapped to the key naming each one's words. */
const FREQUENCY_KEYS: Record<BackupFrequency, string> = {
  "every-6-hours": "every6",
  daily: "daily",
  weekly: "weekly",
};

/**
 * The cron in words.
 *
 * The minute and hour are derived from the database id so the fleet's dumps do
 * not all start at once, which means every customer sees an arbitrary-looking
 * time. Showing `37 2 * * *` invites "why is mine at :37?"; showing "Daily at
 * 02:37 UTC" answers the only question they actually had.
 */
export function describeSchedule(
  frequency: BackupFrequency,
  cron: string,
  t: ReturnType<typeof useTranslation<"database">>["t"],
): string {
  const [minute, hour] = cron.split(" ");
  const key = FREQUENCY_KEYS[frequency] ?? frequency;
  const label = t(`backups.${key}` as "backups.daily");
  if (!minute || !hour) return label;

  const mm = minute.padStart(2, "0");
  if (frequency === "every-6-hours") {
    const start = hour.split("/")[0] ?? "0";
    return t("backups.scheduleFrom", { label, time: `${start.padStart(2, "0")}:${mm}` });
  }
  return t("backups.scheduleAt", { label, time: `${hour.padStart(2, "0")}:${mm}` });
}

/**
 * When the next dump is due, as an ISO timestamp.
 *
 * Walked forward an hour at a time rather than solved, because the shapes the
 * platform emits are few and a loop cannot be subtly wrong about month ends,
 * leap years or the turn of a week — which a closed-form version would be, in a
 * way nobody would notice until the one day a year it mattered.
 *
 * Everything is UTC. The cron is written in UTC and the schedule is displayed in
 * UTC; converting only one of them is how "next run" ends up an hour out for
 * half the year.
 */
export function nextRunAt(cron: string, from: Date = new Date()): string | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  const minute = Number(parts[0]);
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;

  // Either `2` or `2/6` — a fixed hour, or a start and a step.
  const [startText, stepText] = (parts[1] ?? "").split("/");
  const start = Number(startText);
  const step = stepText === undefined ? 0 : Number(stepText);
  if (!Number.isInteger(start) || start < 0 || start > 23) return null;
  if (stepText !== undefined && (!Number.isInteger(step) || step < 1)) return null;

  const weekdayText = parts[4];
  const weekday = weekdayText && weekdayText !== "*" ? Number(weekdayText) : null;
  if (weekday !== null && (!Number.isInteger(weekday) || weekday < 0 || weekday > 6)) return null;

  const cursor = new Date(
    Date.UTC(
      from.getUTCFullYear(),
      from.getUTCMonth(),
      from.getUTCDate(),
      from.getUTCHours(),
      minute,
      0,
      0,
    ),
  );
  // Strictly future: a run happening this very minute is behind us, not ahead.
  if (cursor.getTime() <= from.getTime()) cursor.setUTCHours(cursor.getUTCHours() + 1);

  // Eight days covers the weekly case with a day to spare.
  for (let i = 0; i < 24 * 8; i += 1) {
    const hour = cursor.getUTCHours();
    const hourMatches =
      step > 0 ? (((hour - start) % step) + step) % step === 0 : hour === start;
    const dayMatches = weekday === null || cursor.getUTCDay() === weekday;
    if (hourMatches && dayMatches) return cursor.toISOString();
    cursor.setUTCHours(hour + 1);
  }
  return null;
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] tracking-[0.08em] text-fg-subtle uppercase">{label}</dt>
      <dd className="mt-1 truncate text-sm text-fg">{children}</dd>
    </div>
  );
}

export function BackupPanel({
  orgId,
  database,
  canManage,
  canRestore = false,
}: {
  orgId: string | undefined;
  database: Database;
  canManage: boolean;
  /** `admin` and above: a restore replaces data, as deleting a database does. */
  canRestore?: boolean;
}) {
  const { t } = useTranslation("database");
  const schedule = useBackupSchedule(orgId, database.id);
  const toggle = useSetBackupEnabled(orgId ?? "", database.id);
  const run = useRunBackup(orgId ?? "", database.id);
  const download = useBackupDownload(orgId ?? "", database.id);

  const existing = schedule.data?.data;
  const on = Boolean(existing?.enabled);
  const files = useBackupFiles(orgId, database.id, true);
  const restores = useRestores(orgId, database.id);
  const [restoring, setRestoring] = useState<RestoreChoice | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Which row is fetching a link, so only that row's button spins. */
  const [downloading, setDownloading] = useState<string | null>(null);
  /** Null means untouched, so the stored name shows through without an effect. */
  const [mongoDraft, setMongoDraft] = useState<string | null>(null);

  const isMongo = database.engine === "mongo";
  const mongoName = mongoDraft ?? existing?.databaseName ?? "";
  const mongoChanged = isMongo && on && mongoName.trim() !== (existing?.databaseName ?? "");

  const supported = schedule.data?.supported ?? true;

  if (schedule.isLoading) return <LoadingBlock />;

  if (!supported) {
    return (
      <Alert tone="info">
        <span className="font-medium">{t("backups.redisUnsupported")}</span> {t("backups.redisUnsupportedLine")}
      </Alert>
    );
  }

  async function save(enabled: boolean) {
    setError(null);
    setNotice(null);
    try {
      await toggle.mutateAsync({
        enabled,
        ...(isMongo && mongoName.trim() ? { databaseName: mongoName.trim() } : {}),
      });
      setMongoDraft(null);
      setNotice(
        enabled
          ? t("backups.on")
          : t("backups.off"),
      );
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("backups.failed"));
    }
  }

  async function runNow() {
    setError(null);
    setNotice(null);
    try {
      await run.mutateAsync();
      setNotice(t("backups.started"));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("backups.startFailed"));
    }
  }

  /**
   * Fetches a short-lived link and hands it straight to the browser.
   *
   * `window.location` rather than an anchor click or `fetch`: the response is a
   * file the browser should save, and navigating to it starts a native download.
   * Nothing about the dump passes through JavaScript, so a multi-gigabyte file
   * never has to fit in memory.
   */
  async function downloadFile(key: string) {
    setError(null);
    setNotice(null);
    setDownloading(key);
    try {
      const link = await download.mutateAsync(key);
      window.location.href = link.url;
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : t("backups.linkFailed"),
      );
    } finally {
      setDownloading(null);
    }
  }

  const dumps = files.data?.data ?? [];
  const restoreBusy = (restores.data?.data ?? []).some(
    (r) => r.targetDatabaseId === database.id && (r.status === "saving" || r.status === "restoring"),
  );
  // The schedule's own dumps answer "is it working"; a restore's safety backup
  // is listed and restorable, but a fresh one must not stand in for a schedule
  // that has quietly stopped.
  const scheduled = dumps.filter((file) => !file.beforeRestore);
  const latest = scheduled[0];
  const stored = scheduled.reduce((total, file) => total + file.size, 0);
  const upcoming = existing ? nextRunAt(existing.schedule) : null;

  return (
    <div className="space-y-4">
      {error && <Alert>{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      <div className="overflow-hidden rounded-lg border border-line">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 bg-raised/40 px-4 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            <Switch
              checked={on}
              disabled={!canManage || toggle.isPending}
              onChange={(next) => void save(next)}
              label={t("backups.toggle")}
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg">
                {on ? t("backups.enabled") : t("backups.disabled")}
              </p>
              <p className="mt-0.5 truncate text-[13px] text-fg-subtle">
                {on && existing
                  ? existing.keepLatest
                    ? t("backups.keeps", {
                        schedule: describeSchedule(existing.frequency, existing.schedule, t),
                        count: existing.keepLatest,
                      })
                    : t("backups.keepsEverything", {
                        schedule: describeSchedule(existing.frequency, existing.schedule, t),
                      })
                  : t("backups.description")}
              </p>
            </div>
          </div>

          {on && canManage && (
            <Button variant="secondary" loading={run.isPending} onClick={() => void runNow()}>
              {t("backups.now")}
                            </Button>
          )}
        </div>

        {on && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-4 border-t border-line px-4 py-4 sm:grid-cols-4">
            <Stat label={t("backups.last")}>
              {files.isLoading ? (
                <Skeleton className="h-4 w-20" />
              ) : latest ? (
                <span title={absoluteTime(latest.date)}>{relativeTime(latest.date)}</span>
              ) : (
                <span className="text-fg-subtle">{t("backups.noneYet")}</span>
              )}
            </Stat>
            <Stat label={t("backups.next")}>
              {upcoming ? (
                <span title={absoluteTime(upcoming)}>{relativeTime(upcoming)}</span>
              ) : (
                <span className="text-fg-subtle">—</span>
              )}
            </Stat>
            <Stat label={t("backups.stored")}>
              {files.isLoading ? (
                <Skeleton className="h-4 w-20" />
              ) : scheduled.length ? (
                <span className="tabular-nums">
                  {scheduled.length} · {formatBytes(stored)}
                </span>
              ) : (
                <span className="text-fg-subtle">—</span>
              )}
            </Stat>
            <Stat label={t("backups.cost")}>
              <span className="tabular-nums">+30%</span>
              <span className="text-fg-subtle">{t("backups.ofPlan")}</span>
            </Stat>
          </dl>
        )}
      </div>

      {/*
        MongoDB only, and it is not a preference — mongodump takes one named
        database and Mongo creates databases on first write, so Maxlayer
        provisions none. Without it the dump silently contains nothing, which is
        why it sits beside the toggle rather than behind a disclosure.
      */}
      {isMongo && canManage && (
        <div className="rounded-lg border border-line px-4 py-4">
          <Field
            label={t("backups.which")}
            htmlFor="mongo-backup-db"
            hint={t("backups.whichHint")}
            action={
              mongoChanged ? (
                <Button
                  size="sm"
                  loading={toggle.isPending}
                  disabled={!mongoName.trim()}
                  onClick={() => void save(true)}
                >
                  {t("backups.save")}
                                    </Button>
              ) : undefined
            }
          >
            <Input
              id="mongo-backup-db"
              value={mongoName}
              placeholder={database.slug}
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => setMongoDraft(event.target.value)}
            />
          </Field>
        </div>
      )}

      {restores.data?.data.length ? (
        <RestoreActivity
          database={database}
          restores={restores.data.data}
          canRestore={canRestore}
          onRestore={setRestoring}
        />
      ) : null}

      {(on || dumps.length > 0) && (
        <div className="overflow-hidden rounded-lg border border-line">
          <div className="flex items-baseline justify-between gap-3 border-b border-line px-4 py-3">
            <h4 className="text-[13px] font-medium text-fg">{t("points.title")}</h4>
            {dumps.length > 0 && (
              <span className="text-xs tabular-nums text-fg-subtle">{t("points.count", { count: dumps.length })}</span>
            )}
          </div>

          {files.isLoading ? (
            <div className="space-y-2.5 px-4 py-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : files.data?.unavailable ? (
            <div className="px-4 py-4">
              <Alert tone="warning">{t("backups.listFailed")}</Alert>
            </div>
          ) : dumps.length === 0 ? (
            <EmptyState
              icon="database"
              title={t("backups.empty")}
              description={t("backups.emptyDescription")}
              action={
                canManage ? (
                  <Button variant="secondary" loading={run.isPending} onClick={() => void runNow()}>
                    {t("backups.now")}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <RestorePoints
              dumps={dumps}
              canRestore={canRestore}
              canDownload={canManage}
              restoreBusy={restoreBusy}
              downloading={downloading}
              onRestore={(file) => setRestoring({ key: file.key, name: file.name, date: file.date, size: file.size })}
              onDownload={(key) => void downloadFile(key)}
            />
          )}
        </div>
      )}

      {orgId && (
        <RestoreModal
          orgId={orgId}
          database={database}
          choice={restoring}
          onClose={() => setRestoring(null)}
          onStarted={() => {
            setRestoring(null);
            setNotice(t("restore.started"));
          }}
        />
      )}
    </div>
  );
}
